import { z } from 'zod';
import { IdSchema, E164Schema, TimestampSchema, ListQuerySchema } from './common.js';

export const CarrierSchema = z.object({
  id: IdSchema,
  name: z.string(),
  status: z.enum(['active', 'disabled']),
  rate_deck_id: IdSchema.nullable(),
  trunk_count: z.number().int().optional(),
  created_at: TimestampSchema,
  version: z.number().int(),
});
export type Carrier = z.infer<typeof CarrierSchema>;

export const CarrierCreateSchema = z.object({
  name: z.string().min(1).max(120),
  status: z.enum(['active', 'disabled']).default('active'),
}).strict();

export const TrunkDirectionSchema = z.enum(['inbound', 'outbound', 'both']);
export const TrunkStatusSchema = z.enum(['active', 'disabled', 'draining']);
export const TransportSchema = z.enum(['udp', 'tcp', 'tls']);
export const AuthModeSchema = z.enum(['ip', 'register']);

/**
 * Note the absence of auth_secret_enc / auth_secret_nonce.
 *
 * A trunk password is a credential to someone else's network -- leaking one is
 * both a security incident and a commercial one. It is never joined into this
 * projection, so schema-driven serialisation cannot emit it even by mistake.
 * `auth_secret_set` reports only whether one exists.
 */
export const TrunkSchema = z.object({
  id: IdSchema,
  carrier_id: IdSchema,
  carrier_name: z.string().optional(),
  name: z.string(),
  direction: TrunkDirectionSchema,
  status: TrunkStatusSchema,

  host: z.string(),
  port: z.number().int(),
  transport: TransportSchema,
  use_srv: z.boolean(),
  source_ips: z.array(z.string()),

  auth_mode: AuthModeSchema,
  auth_username: z.string().nullable(),
  auth_realm: z.string().nullable(),
  auth_secret_set: z.boolean(),

  max_concurrent_calls: z.number().int().nullable(),
  max_cps: z.number().int().nullable(),

  outbound_format: z.enum(['e164', 'e164_no_plus', 'national', '00_prefixed']),
  outbound_prefix: z.string().nullable(),
  inbound_strip: z.number().int(),
  inbound_prepend: z.string().nullable(),

  cli_mode: z.enum(['from_only', 'from_and_pai', 'rpid']),
  fallback_cli: E164Schema.nullable(),
  privacy_mode: z.enum(['none', 'id', 'header']),

  allowed_codecs: z.array(z.string()),
  dtmf_mode: z.enum(['rfc2833', 'info', 'inband']),
  t38_enabled: z.boolean(),
  early_media: z.boolean(),

  health_status: z.enum(['unknown', 'up', 'down', 'degraded']),
  last_health_change: TimestampSchema.nullable(),

  created_at: TimestampSchema,
  updated_at: TimestampSchema,
  version: z.number().int(),
});
export type Trunk = z.infer<typeof TrunkSchema>;

const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;

export const TrunkCreateSchema = z.object({
  carrier_id: IdSchema,
  name: z.string().min(1).max(120),
  direction: TrunkDirectionSchema.default('both'),
  host: z.string().min(1).max(253),
  port: z.number().int().min(1).max(65535).default(5060),
  transport: TransportSchema.default('udp'),
  use_srv: z.boolean().default(false),
  source_ips: z.array(z.string().regex(ipv4, 'must be an IPv4 address or CIDR')).default([]),

  auth_mode: AuthModeSchema.default('ip'),
  auth_username: z.string().max(120).optional(),
  auth_realm: z.string().max(253).optional(),
  auth_secret: z.string().min(1).max(256).optional(),

  max_concurrent_calls: z.number().int().min(1).max(10000).optional(),
  max_cps: z.number().int().min(1).max(1000).optional(),

  outbound_format: z.enum(['e164', 'e164_no_plus', 'national', '00_prefixed']).default('e164'),
  outbound_prefix: z.string().max(16).optional(),
  inbound_strip: z.number().int().min(0).max(20).default(0),
  inbound_prepend: z.string().max(16).optional(),

  cli_mode: z.enum(['from_only', 'from_and_pai', 'rpid']).default('from_and_pai'),
  fallback_cli: E164Schema.optional(),
  allowed_codecs: z.array(z.string().max(24)).default(['PCMA', 'PCMU']),
  dtmf_mode: z.enum(['rfc2833', 'info', 'inband']).default('rfc2833'),
  t38_enabled: z.boolean().default(false),
})
  .strict()
  /**
   * An IP-authenticated trunk with no source_ips accepts calls from nobody --
   * or, if the ACL is ever applied permissively by mistake, from everybody.
   * Refusing it here means an open inbound trunk cannot be created by omission.
   */
  .refine((t) => t.auth_mode !== 'ip' || t.direction === 'outbound' || t.source_ips.length > 0, {
    message: 'An inbound IP-authenticated trunk must list at least one source IP',
    path: ['source_ips'],
  })
  .refine((t) => t.auth_mode !== 'register' || !!t.auth_username, {
    message: 'Registration trunks require a username',
    path: ['auth_username'],
  });
export type TrunkCreate = z.infer<typeof TrunkCreateSchema>;

export const TrunkUpdateSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  direction: TrunkDirectionSchema.optional(),
  status: TrunkStatusSchema.optional(),
  host: z.string().min(1).max(253).optional(),
  port: z.number().int().min(1).max(65535).optional(),
  transport: TransportSchema.optional(),
  source_ips: z.array(z.string().regex(ipv4)).optional(),
  max_concurrent_calls: z.number().int().min(1).max(10000).nullable().optional(),
  max_cps: z.number().int().min(1).max(1000).nullable().optional(),
  outbound_prefix: z.string().max(16).nullable().optional(),
  inbound_strip: z.number().int().min(0).max(20).optional(),
  inbound_prepend: z.string().max(16).nullable().optional(),
  fallback_cli: E164Schema.nullable().optional(),
  allowed_codecs: z.array(z.string().max(24)).optional(),
  dtmf_mode: z.enum(['rfc2833', 'info', 'inband']).optional(),
  t38_enabled: z.boolean().optional(),
}).strict();

export const TrunkListQuerySchema = ListQuerySchema.extend({
  carrier_id: IdSchema.optional(),
  status: TrunkStatusSchema.optional(),
  q: z.string().max(200).optional(),
});

/**
 * Number format tester (doc 07 section 4.11).
 *
 * Validates a trunk's transformation rules against real examples BEFORE it can
 * carry traffic. Getting these wrong produces calls that fail at the carrier
 * with an opaque 404, which is miserable to debug from SIP traces alone.
 */
export const NumberFormatTestSchema = z.object({
  numbers: z.array(z.string().min(1).max(24)).min(1).max(20),
  direction: z.enum(['inbound', 'outbound']).default('outbound'),
}).strict();

export const NumberFormatResultSchema = z.object({
  results: z.array(z.object({
    input: z.string(),
    output: z.string(),
    note: z.string().nullable(),
  })),
});
